// The UK nation of a firm, as crm__uk_nation works it out in the database (migration 20261011000024). Used by demo
// mode, which has no database; keep the two in step. From the postcode area; else a nation named as the country;
// else a town or region that names one without doubt. Guernsey, Jersey and the Isle of Man are crown dependencies,
// not UK nations.
export type Nation = "scotland" | "england" | "wales" | "northern_ireland" | "crown_dependency";

export const NATION_LABELS: Record<Nation, string> = {
  scotland: "Scotland",
  england: "England",
  wales: "Wales",
  northern_ireland: "Northern Ireland",
  crown_dependency: "Channel Islands or Isle of Man",
};

const SCOTLAND = ["AB", "DD", "DG", "EH", "FK", "G", "HS", "IV", "KA", "KW", "KY", "ML", "PA", "PH", "ZE"];
const WALES = ["CF", "LD", "LL", "NP", "SA"];
const words = (s: string) => new RegExp(`\\b(${s})\\b`);

// Distinctly British names: used when the country is the UK or not given.
const SCOT = "edinburgh|kirkcaldy|kilmarnock|falkirk|dunfermline|motherwell|east kilbride|cumbernauld|greenock|dumfries|" +
  "fort william|lerwick|kirkwall|stornoway|st andrews|arbroath|peterhead|fraserburgh|coatbridge|glenrothes|galashiels|hawick|" +
  "thurso|coldstream|paxton|peebles|scotland|scottish borders|lanarkshire|ayrshire|argyll|renfrewshire|dunbartonshire|shetland|" +
  "orkney|western isles|perth and kinross|clackmannanshire|aberdeenshire|lothian|dumfries and galloway";
const WALES_T = "cardiff|wrexham|aberystwyth|carmarthen|llanelli|merthyr tydfil|caerphilly|pontypridd|port talbot|llandudno|" +
  "haverfordwest|welshpool|wales|gwynedd|powys|ceredigion|pembrokeshire|carmarthenshire|glamorgan|gwent|anglesey|monmouthshire|" +
  "flintshire|denbighshire|conwy|hay-on-wye";
const NI = "belfast|londonderry|lisburn|newry|ballymena|coleraine|omagh|enniskillen|craigavon|portadown|newtownabbey|" +
  "carrickfergus|dungannon|northern ireland|county antrim|county down|county armagh|county tyrone|fermanagh";
const ENG = "nottingham|leicester|bradford|southampton|newcastle upon tyne|berwick-upon-tweed|berwick upon tweed|" +
  "cornhill-on-tweed|norham|england|east midlands|west midlands|east of england|yorkshire";
// Names shared with places abroad: used only when the country says UK.
const SCOT_UK = "aberdeen|dundee|glasgow|inverness|perth|stirling|paisley|ayr|livingston|elgin|oban|montrose|irvine|airdrie|" +
  "wick|kelso|melrose|highlands?|fife|moray|angus|galloway";
const WALES_UK = "swansea|bridgend|neath|rhyl|brecon|clwyd";
const NI_UK = "derry|armagh|tyrone";
const ENG_UK = "london|manchester|birmingham|leeds|liverpool|sheffield|bristol|coventry|portsmouth|plymouth|reading|oxford|" +
  "cambridge|york|norwich|exeter|brighton|carlisle|north east|north west|south east|south west";

const TEXT = {
  sure: { ni: words(NI), scot: words(SCOT), wales: words(WALES_T), eng: words(ENG) },
  uk: { ni: words(`${NI}|${NI_UK}`), scot: words(`${SCOT}|${SCOT_UK}`), wales: words(`${WALES_T}|${WALES_UK}`), eng: words(`${ENG}|${ENG_UK}`) },
};
const UK = ["GB", "UK", "UNITED KINGDOM", "GREAT BRITAIN", "SCOTLAND", "ENGLAND", "WALES", "NORTHERN IRELAND"];
const NATION_COUNTRY: Record<string, Nation> = { SCOTLAND: "scotland", ENGLAND: "england", WALES: "wales", "NORTHERN IRELAND": "northern_ireland" };

export function ukNation(postcode?: unknown, country?: unknown, city?: unknown, region?: unknown): Nation | null {
  const c = typeof country === "string" ? country.trim().replace(/\s+/g, " ").toUpperCase() : "";
  if (["GG", "JE", "IM", "GUERNSEY", "JERSEY", "ISLE OF MAN"].includes(c)) return "crown_dependency";
  if (c && !UK.includes(c)) return null;
  const pc = String(postcode ?? "").toUpperCase().replace(/\s+/g, "");
  const text = `${String(city ?? "")} | ${String(region ?? "")}`.toLowerCase()
    .replace(/\bnew (york|england|south wales|jersey|hampshire|brunswick)\b/g, " ");
  if (/^[A-Z]{1,2}[0-9][A-Z0-9]?([0-9][A-Z]{2})?$/.test(pc)) {
    const full = /[0-9][A-Z]{2}$/.test(pc) && pc.length >= 5;
    const outward = full ? pc.slice(0, -3) : pc;
    const sector = full ? Number(pc.slice(-3, -2)) : NaN;
    const area = /^[A-Z]+/.exec(outward)![0];
    const district = Number(/^[A-Z]+([0-9]+)/.exec(outward)?.[1] ?? NaN);
    if (["GY", "JE", "IM"].includes(area)) return "crown_dependency";
    if (area === "BT") return "northern_ireland";
    if (SCOTLAND.includes(area)) return "scotland";
    if (area === "TD") {
      if (district === 12) return /\b(coldstream|birgham|swinton)\b/.test(text) ? "scotland" : "england";
      if (district === 15) return /\b(paxton|foulden|hutton)\b/.test(text) ? "scotland" : "england";
      return "scotland";
    }
    if (WALES.includes(area)) return "wales";
    if (area === "SY") return district >= 15 && district <= 25 ? "wales" : "england";
    if (area === "CH") return district >= 5 && district <= 8 ? "wales" : "england";
    if (area === "HR" && district === 3 && sector === 5) return "wales";
    return "england";
  }
  if (NATION_COUNTRY[c]) return NATION_COUNTRY[c]!;
  const t = c ? TEXT.uk : TEXT.sure;
  if (t.ni.test(text)) return "northern_ireland";
  if (t.scot.test(text)) return "scotland";
  if (t.wales.test(text)) return "wales";
  if (t.eng.test(text)) return "england";
  return null;
}
